import { Checkbox, Flex } from '@solinkcorp/solink-components';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import useLocale from 'src/hooks/useLocale';
import styles from './DaysOfWeekPicker.module.sass';

// days of the week in CRON format https://www.ibm.com/docs/en/db2oc?topic=task-unix-cron-format
const DAYS_OF_WEEK = [0, 1, 2, 3, 4, 5, 6] as const;

type DaysOfWeek = (typeof DAYS_OF_WEEK)[number];

interface DaysOfWeekPickerProps {
  // the days that are selected, values have to be in CRON format e.g 0 = sunday
  selectedDaysOfWeek: number[];
  setSelectedDaysOfWeek: React.Dispatch<React.SetStateAction<number[]>>;
  disabled?: boolean;
}

const DaysOfWeekPicker: React.FC<DaysOfWeekPickerProps> = ({
  selectedDaysOfWeek,
  setSelectedDaysOfWeek,
  disabled = false,
}) => {
  const locale = useLocale();

  const dayOfWeekToTextMap: { [key in DaysOfWeek]: string } = useMemo(() => {
    const { sunday, monday, tuesday, wednesday, thursday, friday, saturday } =
      locale.notificationsPage;

    return {
      0: sunday,
      1: monday,
      2: tuesday,
      3: wednesday,
      4: thursday,
      5: friday,
      6: saturday,
    };
  }, [locale.notificationsPage]);

  const handleCheckboxChange = (dayOfWeek: number, newValue: boolean) => {
    /**
     * we sort the new days of week array so string equivalents are displayed in order of sunday->saturday
     * instead of order of selection. The sorted days of week are saved in the activity's CRON string
     */
    if (newValue) {
      setSelectedDaysOfWeek([...selectedDaysOfWeek, dayOfWeek].sort());
    } else {
      setSelectedDaysOfWeek(selectedDaysOfWeek.filter((day) => day !== dayOfWeek).sort());
    }
  };

  const chexboxesWrapperClassnames = classNames('days-of-week__checkboxes-wrapper', {
    disabled,
  });

  return (
    <Flex gap="12px" className={chexboxesWrapperClassnames}>
      {DAYS_OF_WEEK.map((dayOfWeekNumber) => {
        const isChecked = selectedDaysOfWeek.includes(dayOfWeekNumber);
        return (
          <Checkbox
            name={`day-of-week-${dayOfWeekNumber}`}
            className={styles.dayOfWeekCheckbox}
            key={dayOfWeekNumber}
            checked={selectedDaysOfWeek.includes(dayOfWeekNumber)}
            labelText={dayOfWeekToTextMap[dayOfWeekNumber]}
            onChange={() => {
              handleCheckboxChange(dayOfWeekNumber, !isChecked);
            }}
            disabled={disabled}
          />
        );
      })}
    </Flex>
  );
};

export default DaysOfWeekPicker;
